<script setup lang="ts">
import {ref, watch, computed} from 'vue'
import {useMagicKeys} from '@vueuse/core'
import axios from 'axios'
import {useStore} from 'vuex'
import {useRouter} from 'vue-router'
import {debounce} from 'lodash'
import {bbox} from '@turf/turf'
import {API, HOST} from '@/utils/http'
import {Avatar, AvatarFallback, AvatarImage} from '@/components/ui/avatar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Input } from '@/components/ui/input'
import { Search, MapPin, FolderDot, Building2, Tag, Navigation, X } from 'lucide-vue-next'

const access_token = 'pk.eyJ1IjoibW5ybG1uc3RyIiwiYSI6ImNsc2FibmNrODAyNDMycW9jbDg1a2JoczAifQ.EJdbYQ9kSL1KSymYP0v3Bg'

interface Suggestion {
  id?: string | number;
  name: string;
  type?: string;
  suggestion_type?: string;
  mapbox_id?: string;
  geometry?: any;
  avatar_url?: string;
  [key: string]: any;
}

const router = useRouter()
const store = useStore()
const member = computed(() => store.getters['user/getMember'])
const map = computed(() => store.getters['geo/getMap'])

const open = ref(false)
const keys = useMagicKeys()
const esc = keys['escape']
const searchValue = ref('')

const suggestions = ref<Suggestion[]>([])
const sortedSuggestions = computed(() => {
  return suggestions.value.sort((a, b) => {
    if (a.name === null) return -1
    return a.name.localeCompare(b.name)
  })
})

const handleOpenChange = (isOpen: boolean) => {
  open.value = isOpen
}

const searchMapbox = async (value: string) => {
  cleanMapboxSuggestions()

  try {
    const response = await axios.get('https://api.mapbox.com/search/searchbox/v1/suggest', {
      params: {
        q: value,
        access_token: access_token,
        session_token: member.value.code,
        language: 'en',
        limit: 2,
      }
    })

    for (const suggestion of response.data.suggestions) {
      suggestions.value.push({
        suggestion_type: 'mapbox',
        ...suggestion
      })
    }

  } catch (error) {
    cleanMapboxSuggestions()
  }
}

const cleanMapboxSuggestions = () => {
  suggestions.value = suggestions.value.filter(suggestion => suggestion.suggestion_type !== 'mapbox')
}

const searchApi = async (value: string) => {
  try {
    const apiParams: Record<string, any> = {
      q: value,
    }

    const response = await API({
      url: `${HOST}/search`,
      method: 'GET',
      params: apiParams
    })

    for (const suggestion of response.data.suggestions) {
      suggestions.value.push({
        suggestion_type: 'api',
        ...suggestion
      })
    }
  } catch (error) {
    console.error(error)
  }
}

const search = debounce(async (e: Event) => {
  suggestions.value = []
  const value = (e.target as HTMLInputElement).value
  searchValue.value = value

  if (value.length === 0) {
    open.value = false
    return
  }

  open.value = true
  await searchMapbox(value)
  await searchApi(value)
}, 300)

const focusOnMapboxResult = async (suggestion: Suggestion) => {
  try {
    const response = await axios.get(`https://api.mapbox.com/search/searchbox/v1/retrieve/${suggestion.mapbox_id}`, {
      params: {
        access_token: access_token,
        session_token: member.value.code,
      }
    })

    const feature = response.data.features[0]
    const bounds = bbox(feature)
    map.value.fitBounds(bounds, {
      padding: 100,
      duration: 100
    })
    open.value = false
  } catch (error) {
    console.error(error)
  }
}

const focusOnFeature = (suggestion: Suggestion) => {
  const bounds = bbox(suggestion.geometry)
  map.value.fitBounds(bounds, {
    padding: 100,
    duration: 100
  })
}

const focusOnResult = async (suggestion: Suggestion) => {
  if (suggestion.suggestion_type === 'mapbox') {
    await focusOnMapboxResult(suggestion)
  } else {
    if (suggestion.type === 'feature') {
      focusOnFeature(suggestion)
      console.log('feature', suggestion)
    } else if (suggestion.type === 'project') {
      router.push({
        name: 'Project',
        params: {
          project_id: suggestion.id
        }
      })
    } else if (suggestion.type === 'organization') {
      console.log('organization', suggestion)
    } else if (suggestion.type === 'tag') {
      store.commit('search/ADD_PARAM', {
        key: 'tag_ids',
        value: suggestion.id
      })
    } else if (suggestion.type === 'member') {
      store.commit('search/ADD_PARAM', {
        key: 'member_ids',
        value: suggestion.id
      })
    }
  }

  suggestions.value = []
  open.value = false
  searchValue.value = suggestion.name
}

const clearSearch = () => {
  searchValue.value = ''
  suggestions.value = []
  open.value = false
}

watch(esc, (v) => {
  if (v) open.value = false
})
</script>

<template>
  <Popover :open="open" @update:open="handleOpenChange">
    <PopoverTrigger as-child>
      <div class="relative h-full flex items-center">
        <Input
          class="h-full rounded-lg xl:w-[350px]"
          placeholder="Search"
          @input="search"
          v-model="searchValue"
        />
        <div class="absolute right-3 cursor-pointer" @click="searchValue ? clearSearch() : null">
          <X v-if="searchValue" class="h-4 w-4 opacity-50" />
          <Search v-else class="h-4 w-4 opacity-50" />
        </div>
      </div>
    </PopoverTrigger>
    <PopoverContent class="w-[400px] p-0" align="start">
      <div v-if="sortedSuggestions?.length" class="max-h-[300px] overflow-y-auto p-1">
        <div class="px-2 py-1.5 text-sm font-medium text-gray-500">Results</div>
        <div
          v-for="(item, index) in sortedSuggestions"
          :key="index"
          class="relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
          @click="focusOnResult(item)"
        >
          <div class="w-5 h-5 flex items-center justify-center mr-2">
            <MapPin v-if="item.suggestion_type === 'mapbox'" class="h-4 w-4 text-gray-400" />
            <FolderDot v-if="item.type === 'project'" class="h-4 w-4 text-gray-400" />
            <Building2 v-if="item.type === 'organization'" class="h-4 w-4 text-gray-400" />
            <Tag v-if="item.type === 'tag'" class="h-4 w-4 text-gray-400" />
            <Navigation v-if="item.type === 'feature'" class="h-4 w-4 text-gray-400" />
            <Avatar class="w-5 h-5" v-if="item.type === 'member'">
              <AvatarImage v-if="item.avatar_url" :src="item.avatar_url" alt="member" />
              <AvatarFallback></AvatarFallback>
            </Avatar>
          </div>
          <span>{{ item.name }}</span>
        </div>
      </div>
      <div v-else-if="suggestions.length === 0" class="p-4 text-center text-sm text-gray-500">
        Type to search...
      </div>
    </PopoverContent>
  </Popover>
</template>
